import { storageKey } from '../config';

// Resolved per call: the app sets its storage prefix after importing app-core.
const tokenKey = (): string => storageKey('token');
const expiryKey = (): string => storageKey('tokenExpiry');

type Listener = () => void;

const read = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null): void => {
  try {
    if (value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
  } catch {
    // Storage unavailable: keep the token in memory only.
  }
};

const parseDate = (value: string | null): Date | null => {
  if (value === null) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

let token: string | null = null;
let expiry: Date | null = null;
let loaded = false;
const listeners = new Set<Listener>();

/**
 * Reads the sessionStorage mirror on first use rather than at import, so the app's
 * `configureAppCore` call (made after its imports) decides which keys are read.
 */
const load = (): void => {
  if (loaded) {
    return;
  }
  loaded = true;
  token = read(tokenKey());
  expiry = parseDate(read(expiryKey()));
};

const notify = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

const clear = (): void => {
  load();
  if (token === null && expiry === null) {
    return;
  }
  token = null;
  expiry = null;
  write(tokenKey(), null);
  write(expiryKey(), null);
  notify();
};

/**
 * Holds the backend JWT in memory with a sessionStorage mirror so a reload keeps the
 * session. An expired token reads as absent (parity with OperationsTokenExpiryMiddleware).
 */
export const tokenStore = {
  getToken: (): string | null => {
    load();
    if (token !== null && expiry !== null && expiry.getTime() <= Date.now()) {
      clear();
    }
    return token;
  },
  getExpiry: (): Date | null => {
    load();
    return expiry;
  },
  setToken: (value: string, expiresAt: Date): void => {
    loaded = true;
    token = value;
    expiry = expiresAt;
    write(tokenKey(), value);
    write(expiryKey(), expiresAt.toISOString());
    notify();
  },
  clear,
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }
};
